
import React, { useState, useEffect } from 'react';
import { ArrowRight, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { getSubTopicsForTopic } from '@/utils/questionUtils';
import { questions } from '@/data/questions';
import { SubTopic } from '@/utils/questionUtils';

export interface Topic {
  id: string;
  title: string;
  arabicTitle: string;
  description: string;
  questionCount: number;
}

interface TopicSelectorProps {
  topics: Topic[];
  onSelectTopic: (topicId: string, subtopicId?: string) => void;
}

const TopicSelector: React.FC<TopicSelectorProps> = ({ topics, onSelectTopic }) => {
  const [selectedSubtopic, setSelectedSubtopic] = useState<string | null>(null);
  const [subtopics, setSubtopics] = useState<SubTopic[]>([]);
  
  const tajweedTopic = topics.find(topic => topic.id === 'taj101');
  
  useEffect(() => {
    if (tajweedTopic) {
      // Get subtopics for Tajweed
      const fetchedSubtopics = getSubTopicsForTopic(questions, 'taj101');
      
      // Log to debug
      console.log('Number of available tajweed questions:', questions.filter(q => q.topic === 'taj101').length);
      console.log('Raw tajweed question IDs:', questions.filter(q => q.topic === 'taj101').map(q => q.id).slice(0, 10));
      console.log('Extracted subtopics:', fetchedSubtopics);
      
      // Sort subtopics alphabetically
      fetchedSubtopics.sort((a, b) => a.title.localeCompare(b.title));
      
      // Add "All Tajweed" option
      const allTajweedOption: SubTopic = {
        id: "all",
        title: "All Tajweed Topics",
        questionCount: tajweedTopic.questionCount || 0
      };
      
      setSubtopics([allTajweedOption, ...fetchedSubtopics]);
    }
  }, [tajweedTopic]);

  const handleTopicClick = (topicId: string) => {
    if (topicId === 'taj101') {
      // For Tajweed, use the selected subtopic or default to "all"
      const subtopicToUse = selectedSubtopic || 'all';
      onSelectTopic(topicId, subtopicToUse === 'all' ? undefined : subtopicToUse);
    } else {
      // For other topics, continue with normal behavior
      onSelectTopic(topicId);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <h2 className="text-3xl font-serif font-bold mb-8 text-center">
        Choose a Topic to Practice
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {topics.map((topic) => (
          <Card 
            key={topic.id}
            className={cn(
              "cursor-pointer hover:shadow-soft-lg transition-all-soft",
              topic.id === 'taj101' && "relative"
            )}
            onClick={() => topic.id !== 'taj101' && handleTopicClick(topic.id)}
          >
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{topic.title}</CardTitle>
                  <div className="text-arabic mt-1 text-lg">{topic.arabicTitle}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="mt-2 text-muted-foreground">
                {topic.description}
              </CardDescription>
              
              <div className="flex justify-between items-center mt-4">
                <div className="flex items-center text-sm text-muted-foreground">
                  <BookOpen className="mr-1 h-4 w-4" />
                  {topic.questionCount} questions
                </div>
                
                {topic.id === 'taj101' ? (
                  <div className="flex flex-col space-y-2 w-full">
                    <Select
                      value={selectedSubtopic || 'all'}
                      onValueChange={(value) => setSelectedSubtopic(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a Tajweed topic" />
                      </SelectTrigger>
                      <SelectContent>
                        {subtopics.map((subtopic) => (
                          <SelectItem key={subtopic.id} value={subtopic.id}>
                            {subtopic.title} ({subtopic.questionCount} questions)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <button
                      onClick={() => handleTopicClick('taj101')}
                      className="flex items-center justify-center text-sm font-medium text-primary hover:text-primary/80 transition-colors w-full"
                    >
                      Start practicing
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                    Start practicing
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TopicSelector;
